import React from "react";
import {
  Box,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  InputAdornment,
  IconButton,
  Divider,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Paper,
  Typography,
  Button,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  Save as SaveIcon,
  AddCircleOutline as AddCircleOutlineIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { formatCamelCase } from "../../../../shared/utils/formatter";
import {
  State,
  Action,
  ConfigurationPreset,
  ActionType,
} from "../../../../shared/interfaces/kpi";
import { uniqueId } from "../../../../shared/utils/kpi";
import "./styles.css";

interface PresetListPanelProps {
  state: State;
  dispatch: React.Dispatch<Action>;
  dateOptions: Record<string, boolean>;
  selectedPreset: ConfigurationPreset;
  setDeleteDialogOpen: (open: boolean) => void;
  setPresetToDelete: (id: string | null) => void;
  isPresetSaveDisabled: boolean;
}

const PresetListPanel: React.FC<PresetListPanelProps> = ({
  state,
  dispatch,
  dateOptions,
  selectedPreset,
  setDeleteDialogOpen,
  setPresetToDelete,
  isPresetSaveDisabled,
}) => {
  const theme = useTheme();

  const renderDateOptions = (options: Record<string, boolean>) => (
    <RadioGroup
      row={window.innerWidth > 600}
      value={state.editingPresetDate || ""}
      onChange={(e) =>
        dispatch({ type: ActionType.UPDATE_PRESET_DATE, date: e.target.value })
      }
    >
      {Object.entries(options)
        .filter(([_, v]) => v)
        .map(([key]) => (
          <FormControlLabel
            key={key}
            value={key}
            control={<Radio size="small" />}
            label={formatCamelCase(key).toLowerCase()}
            sx={{
              mr: 2,
              "& .MuiFormControlLabel-label": { fontSize: "0.8rem" },
            }}
          />
        ))}
    </RadioGroup>
  );

  const handleNewPreset = () => {
    dispatch({
      type: ActionType.NEW_PRESET,
      id: uniqueId(),
      preset: {
        name: `New Preset ${Object.keys(state.savedConfigurations).length + 1}`,
        date: state.editingPresetDate,
        kpiSettings: {},
        stayDate: state.sharedStayDate,
      },
    });
  };

  return (
    <Box
      sx={{
        width: { xs: "100%", md: 250 },
        p: 1,
        borderRight: 1,
        borderColor: "divider",
        bgcolor: { xs: "transparent", md: theme.palette.grey[100] },
      }}
    >
      <Button
        fullWidth
        variant="outlined"
        startIcon={<AddCircleOutlineIcon />}
        sx={{ mb: 1, fontSize: "0.8rem", textTransform: "none" }}
        disabled={Object.keys(state.savedConfigurations).length >= 5}
        onClick={handleNewPreset}
        className="newPresetBtn"
      >
        New Preset
      </Button>
      <Divider sx={{ mb: 1 }} />
      <Typography
        variant="subtitle2"
        sx={{
          pl: 1,
          mb: 1,
          bgcolor: { xs: "transparent", md: theme.palette.grey[100] },
          fontSize: "0.8rem",
        }}
      >
        SAVED PRESETS
      </Typography>
      <List dense sx={{ maxHeight: 200, overflowY: "auto" }}>
        {Object.entries(state.savedConfigurations).map(([id, cfg]) => (
          <ListItemButton
            key={id}
            onClick={() =>
              dispatch({ type: ActionType.SELECT_PRESET, id, preset: cfg })
            }
            sx={{
              borderRadius: 1,
              mb: 0.3,
              bgcolor:
                state.selectedConfigId === id
                  ? theme.palette.primary.light + "33"
                  : "transparent",
              "@media (max-width: 600px)": {
                borderColor:
                  state.selectedConfigId === id ? "primary.main" : "divider",
                border: 1,
              },
            }}
            className={`mobPresetDel ${
              state.selectedConfigId === id ? "selected" : ""
            }`}
          >
            <ListItemText
              primary={cfg.name}
              primaryTypographyProps={{
                fontWeight: state.selectedConfigId === id ? "bold" : "normal",
                variant: "body2",
                fontSize: "0.8rem",
              }}
            />
            {Object.keys(state.savedConfigurations).length > 1 && (
              <IconButton
                size="small"
                color="error"
                onClick={(e) => {
                  e.stopPropagation();
                  setPresetToDelete(id);
                  setDeleteDialogOpen(true);
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
          </ListItemButton>
        ))}
      </List>

      {/* Preset Name Editor */}
      <TextField
        fullWidth
        size="small"
        label="Preset Name"
        value={selectedPreset.name}
        className="mobPreset"
        onChange={(e) =>
          dispatch({ type: ActionType.SAVE_PRESET, name: e.target.value })
        }
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton size="small" disabled={isPresetSaveDisabled}>
                <SaveIcon fontSize="small" color="primary" />
              </IconButton>
            </InputAdornment>
          ),
          sx: { fontSize: "0.9rem" },
        }}
        sx={{ py: 0.5, mt: 1, fontSize: "0.8rem" }}
      />

      {/* Date Range Selector */}
      <Paper
        elevation={0}
        sx={{
          p: 1,
          mt: 2,
          borderRadius: "12px",
          bgcolor: { xs: "transparent", md: theme.palette.grey[100] },
        }}
      >
        <FormControl sx={{ width: "100%" }} className="presetBulletForm">
          <FormLabel
            sx={{
              mb: 1,
              fontSize: "0.8rem",
              bgcolor: { xs: "transparent", md: theme.palette.grey[100] },
            }}
          >
            SELECT DATE RANGE
          </FormLabel>
          {renderDateOptions(dateOptions)}
        </FormControl>
      </Paper>
    </Box>
  );
};

export default PresetListPanel;
